import * as echarts from 'echarts/core';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import type { EChartsOption } from 'echarts';
import type { CSSProperties, RefObject } from 'react';
import {
  breakpointsMediaQuery,
  getColor,
  tooltipFormatter,
  useMediaQuery
} from '@/helpers/utils';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { useCallback } from 'react';
import {
  createShouldSetEchartOption,
  useTooltipHide
} from '@/hooks/useTooltipHide';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer,
  LegendComponent
]);

type BarChartSeries = {
  name: string;
  data: unknown[];
  itemStyle?: Record<string, unknown>;
};

type MainBarChartProps = {
  height?: CSSProperties['height'];
  categories?: Array<string | number>;
  series: BarChartSeries[];
  chartRef: RefObject<ReactEChartsCore | null>;
  min?: number;
  tooltipFunc?: typeof tooltipFormatter;
};

const MainBarChart = ({
  height,
  categories,
  series,
  chartRef,
  min = 0,
  tooltipFunc
}: MainBarChartProps) => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  useTooltipHide(chartRef);
  const shouldSetOption = useCallback(createShouldSetEchartOption(chartRef), [
    chartRef
  ]);

  const chart_series = series.map(item => {
    return {
      type: 'bar',
      name: item.name,
      data: item.data,
      stack: 'one',
      itemStyle: item.itemStyle,
      showSymbol: false,
      symbol: 'circle',
      smooth: false,
      emphasis: {
        scale: true
      }
    };
  });

  /*کانفیگ های مربوط به نمودار */
  const options = ({
    textStyle: {
      fontFamily: 'dana'
    },

    legend: {
      show: false
    },

    tooltip: {
      triggerOn: isMdUp ? 'mousemove' : 'click',
      trigger: 'axis',

      padding: [7, 10],
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('gray-1100') },
      borderWidth: 1,
      formatter: tooltipFunc ? tooltipFunc : tooltipFormatter,
      transitionDuration: 0,
      axisPointer: {
        type: 'none'
      }
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLine: {
        lineStyle: {
          color: getColor('gray-300'),
          type: 'solid'
        }
      },
      axisTick: { show: false },
      axisLabel: { show: false },
      splitLine: {
        show: false
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        show: true,
        color: getColor('gray-800'),
        margin: 5
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: getColor('gray-200')
        }
      },
      axisTick: { show: false },
      axisLine: { show: false },
      min
    },
    series: chart_series,
    grid: { right: '3%', left: '10%', bottom: '10%', top: '5%' }
  } as unknown) as EChartsOption;

  return (
    <ReactEChartsCore
      ref={chartRef}
      echarts={echarts}
      option={options}
      shouldSetOption={shouldSetOption}
      style={{ height }}
    />
  );
};

export default MainBarChart;
